import React, { useEffect, useRef, useState } from "react";
import apiRoutes from "../../api/apiRoutes";
import { apiFetchJson } from "../../utils/http";

const PAGE_SIZE = 500;
const DEBOUNCE_MS = 300;

export default function FacturaClienteAutocomplete({
  value,
  onChange,
  disabled = false,
  placeholder = "Buscar cliente / liquidación...",
}) {
  const boxRef = useRef(null);
  const debounceRef = useRef(null);

  // ============================
  //        ESTADOS
  // ============================
  const [open, setOpen] = useState(false);
  const [inputText, setInputText] = useState("");
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);

  // ============================
  //   VALUE → INPUT
  // ============================
  useEffect(() => {
    if (value && value.label) {
      setInputText(value.label);
    }
    if (!value) {
      setInputText("");
    }
  }, [value]);

  // ============================
  //   CLICK AFUERA → CERRAR
  // ============================
  useEffect(() => {
    const handleOutside = (event) => {
      if (boxRef.current && !boxRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  // ============================
  //        FETCH
  // ============================
  const fetchItems = async ({ pageIndex = 0, append = false, query }) => {
    if (loading) return;

    setLoading(true);

    const url = apiRoutes.facturaLiquidacionCliente.autocompleteUrl({
      q: query,
      page: pageIndex,
      size: PAGE_SIZE,
    });

    try {
      const response = await apiFetchJson(url);
      const responseItems = response?.items || [];

      const more =
        typeof response?.hasMore === "boolean"
          ? response.hasMore
          : responseItems.length === PAGE_SIZE;

      setItems((prev) => (append ? [...prev, ...responseItems] : responseItems));
      setHasMore(more);
      setPage(pageIndex);
    } catch {
      if (!append) {
        setItems([]);
        setHasMore(false);
      }
    } finally {
      setLoading(false);
    }
  };

  // ============================
  //        INPUT (debounce)
  // ============================
  const handleInputChange = (event) => {
    const text = event.target.value;
    setInputText(text);
    setOpen(true);

    // solo limpiamos selección si queda vacío
    if (value && text.trim() === "") {
      onChange(null);
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchItems({
        pageIndex: 0,
        append: false,
        query: text.trim(),
      });
    }, DEBOUNCE_MS);
  };

  const handleFocus = () => {
    setOpen(true);

    if (items.length === 0 && !loading) {
      fetchItems({
        pageIndex: 0,
        append: false,
        query: inputText.trim(),
      });
    }
  };

  // ============================
  //     SCROLL INFINITO
  // ============================
  const handleScroll = (event) => {
    const el = event.currentTarget;
    const nearBottom =
      el.scrollTop + el.clientHeight >= el.scrollHeight - 40;

    if (nearBottom && hasMore && !loading) {
      fetchItems({
        pageIndex: page + 1,
        append: true,
        query: inputText.trim(),
      });
    }
  };

  // ============================
  //      SELECCIONAR
  // ============================
  const handleSelect = (item) => {
    onChange(item);
    setInputText(item.label ?? "");
    setOpen(false);
  };

  // ============================
  //        LIMPIAR
  // ============================
  const clearSelection = () => {
    onChange(null);
    setInputText("");
    setItems([]);
    setOpen(false);
  };

  // ============================
  //        RENDER
  // ============================
  return (
    <div ref={boxRef} className="relative">
      <input
        type="text"
        className="input"
        disabled={disabled}
        placeholder={placeholder}
        value={inputText}
        onChange={handleInputChange}
        onFocus={handleFocus}
      />

      {value && (
        <button
          type="button"
          onClick={clearSelection}
          className="absolute right-3 top-2 text-gray-400"
          title="Limpiar"
        >
          ×
        </button>
      )}

      {open && (
        <div
          className="absolute z-20 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg max-h-64 overflow-auto text-sm"
          onScroll={handleScroll}
        >
          {loading && items.length === 0 && (
            <div className="px-3 py-2 text-gray-500">
              Cargando…
            </div>
          )}

          {!loading && items.length === 0 && (
            <div className="px-3 py-2 text-gray-500">
              Sin resultados.
            </div>
          )}

          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              className="block w-full text-left px-3 py-2 hover:bg-emerald-50"
              onClick={() => handleSelect(item)}
            >
              <div className="text-sm font-medium">
                {item.label}
              </div>
            </button>
          ))}

          {loading && items.length > 0 && (
            <div className="px-3 py-2 text-gray-400 text-xs">
              Cargando más…
            </div>
          )}
        </div>
      )}
    </div>
  );
}
